import React, {useEffect, useState} from "react";
import {Box, CircularProgress, Typography} from "@mui/material";
import {listReleasedLots, ReleasedLot} from "../util/api";

/**
 * The lots of material released to the person signed in.
 *
 * THE PHYSICAL HALF OF THIS PORTAL. The documents screen beside it answers "what software is in
 * the thing we bought"; this one answers "what batch actually arrived, and where is its paperwork"
 * -- which is the question a receiving department asks of every delivery, every day.
 *
 * THE LOT NUMBER LEADS, not the name. A recipient matches a pallet to a record by the number
 * printed on the box, and a recall notice names that number and nothing else; a row that led with
 * "Pump Housing" would be unmatchable against the one piece of paper they are holding.
 *
 * NO SEARCH, NO FILTERS, NO BROWSING, as next door. There is no way to reach a lot nobody chose to
 * release, and a search box would imply there is something to search for.
 */
const HEADING = "#2B2B2B";
const MUTED = "#717171";
const BORDER = "#E0E0E0";

function when(value?: string | null): string {
    if (!value) return "not recorded";
    const at = new Date(String(value).replace(" ", "T"));
    if (Number.isNaN(at.getTime())) return String(value).slice(0, 10);
    return at.toLocaleDateString(undefined, {day: "numeric", month: "short", year: "numeric"});
}

export function amount(one: {Quantity: number | string | null; UnitOfMeasure: string | null}) {
    // A BARE NUMBER IS NOT A QUANTITY, so a quantity with no unit is not shown as one. The service
    // refuses to store that shape; this is the second half of the same rule, for rows written
    // before it existed.
    if (one.Quantity == null || !one.UnitOfMeasure) return null;
    return `${one.Quantity} ${one.UnitOfMeasure}`;
}

export default function Lots({onOpen}: {onOpen: (one: ReleasedLot) => void}) {
    const [rows, setRows] = useState<ReleasedLot[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        listReleasedLots().then(result => {
            if (result.status === 200) {
                setRows(result.data?.Results ?? []);
                setError(null);
            } else {
                // THE CODE IS SHOWN rather than swallowed: "nothing has been released to you" and
                // "we could not reach the server" send the reader to different people.
                setError(result.data?.Cause || result.data?.Code || "That list could not be loaded.");
            }
            setLoading(false);
        });
    }, []);

    if (loading) {
        return (
            <Box sx={{display: "flex", justifyContent: "center", py: 10}}>
                <CircularProgress size={24}/>
            </Box>
        );
    }

    return (
        <Box sx={{maxWidth: 900, mx: "auto", px: 3, py: 6, boxSizing: "border-box"}}>
            <Typography component="h1" sx={{fontSize: 24, fontWeight: 500, color: HEADING}}>
                Your lots
            </Typography>
            <Typography sx={{fontSize: 14, color: MUTED, mt: 1, lineHeight: 1.6}}>
                The batches of material shipped to you, with the certificates that came with them.
            </Typography>

            {error && <Typography sx={{fontSize: 14, color: "#B3261E", mt: 4}}>{error}</Typography>}

            {/* NOTHING YET IS NOT AN ERROR, and the sentence says who to ask rather than leaving
                somebody looking at an empty page wondering whether it is broken. */}
            {!error && rows.length === 0 && (
                <Typography sx={{fontSize: 14, color: MUTED, mt: 4, lineHeight: 1.6}}>
                    No lots have been released to you yet. The company you bought from decides what
                    appears here &mdash; ask your contact there to release the batch you are
                    looking for.
                </Typography>
            )}

            <Box sx={{mt: 4, display: "flex", flexDirection: "column", gap: 1.5}}>
                {rows.map(one => (
                    <Box key={one.USID}
                         onClick={() => onOpen(one)}
                         sx={{
                             border: `1px solid ${BORDER}`, borderRadius: 2, p: 2.5,
                             cursor: "pointer", backgroundColor: "#FFFFFF",
                             "&:hover": {borderColor: "#1498D8"},
                         }}>
                        <Typography sx={{fontSize: 16, fontWeight: 600, color: HEADING}}>
                            {one.LotNumber || one.Name || "Untitled lot"}
                        </Typography>
                        <Typography sx={{fontSize: 13, color: MUTED, mt: 0.5}}>
                            {one.LotNumber && one.Name ? one.Name : null}
                            {one.LotNumber && one.Name && " · "}
                            {one.From ? `From ${one.From}` : "From your supplier"}
                            {amount(one) && ` · ${amount(one)}`}
                        </Typography>
                        <Typography sx={{fontSize: 12, color: MUTED, mt: 1}}>
                            Shipped {when(one.ShippedDatetime)}
                            {one.ShipmentReference && ` on ${one.ShipmentReference}`}
                            {" · "}
                            {/* THE COUNT, NEVER A RESTATEMENT OF IT. "Certificates available"
                                against a zero would read as a system that lost them; "no documents
                                were released with this lot" is the true sentence and says whose
                                decision it was. */}
                            {one.DocumentCount
                                ? `${one.DocumentCount} document${one.DocumentCount === 1 ? "" : "s"}`
                                : "no documents were released with this lot"}
                        </Typography>
                    </Box>
                ))}
            </Box>
        </Box>
    );
}
